
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AppNavbar } from '@/components/layout/AppNavbar';
import { DirectoryHeader } from '@/components/directory/DirectoryHeader';
import { DirectoryFilters } from '@/components/directory/DirectoryFilters';
import { DirectoryResults } from '@/components/directory/DirectoryResults';
import { ChemicalFilters, ChemicalFilters as ChemicalFiltersType } from '@/components/directory/ChemicalFilters';
import { ChemicalResults } from '@/components/directory/ChemicalResults';
import { BenchmarkFilters, BenchmarkFilters as BenchmarkFiltersType } from '@/components/directory/BenchmarkFilters';
import { BenchmarkTable } from '@/components/directory/BenchmarkTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { Product } from '@/types/portfolio';
import { mockGreenSealProducts } from '@/data/mockProducts';
import { benchmarkProducts, stateRanges, BenchmarkProduct } from '@/data/benchmarkData';

export type ViewMode = 'card' | 'list' | 'benchmark';
export type DirectoryTab = 'products' | 'chemicals';

export interface DirectoryFilters {
  search: string;
  certificationStatus: string;
  productType: string;
  manufacturer: string;
}

const Directory = () => {
  const [activeTab, setActiveTab] = useState<DirectoryTab>('products');
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [filters, setFilters] = useState<DirectoryFilters>({
    search: '',
    certificationStatus: '',
    productType: '',
    manufacturer: ''
  });
  
  const [chemicalFilters, setChemicalFilters] = useState<ChemicalFiltersType>({
    search: '',
    chemicalType: '',
    supplier: '',
    hazardLevel: ''
  });
  
  const [benchmarkFilters, setBenchmarkFilters] = useState<BenchmarkFiltersType>({
    search: '',
    productType: '',
    manufacturer: '',
    ecolabels: [],
    retailers: [],
    stateRange: ''
  });

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['directory-products', filters],
    queryFn: async () => {
      let allProducts: Product[] = [];
      
      // Always include mock products as they represent certified Green Seal products
      allProducts = [...mockGreenSealProducts] as Product[];

      try {
        // Try to fetch additional products from database
        let query = supabase
          .from('products')
          .select(`
            *,
            portfolios!inner(
              title,
              user_id
            )
          `)
          .eq('certification_status', 'certified');

        const { data: dbProducts, error } = await query.order('created_at', { ascending: false });
        
        if (!error && dbProducts && dbProducts.length > 0) {
          // Add database products to the mock products
          allProducts = [...allProducts, ...dbProducts as Product[]];
        }
      } catch (err) {
        console.warn('Error fetching database products, using mock data only:', err);
      }

      // Apply filters to all products
      let filteredProducts = allProducts;

      if (filters.search) {
        filteredProducts = filteredProducts.filter(product => 
          product.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
          product.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
          product.manufacturer?.toLowerCase().includes(filters.search.toLowerCase())
        );
      }

      if (filters.productType) {
        filteredProducts = filteredProducts.filter(product => 
          product.product_type === filters.productType
        );
      }

      if (filters.manufacturer) {
        filteredProducts = filteredProducts.filter(product => 
          product.manufacturer?.toLowerCase().includes(filters.manufacturer.toLowerCase())
        );
      }

      return filteredProducts;
    }
  });

  const { data: filteredBenchmarkProducts = [] } = useQuery({
    queryKey: ['benchmark-products', benchmarkFilters],
    queryFn: async () => {
      try {
        // Fetch benchmark products with their related data
        let query = supabase
          .from('benchmark_products')
          .select(`
            *,
            benchmark_product_ecolabels (
              ecolabel_name,
              status
            ),
            benchmark_product_retailers (
              retailer_name,
              status
            )
          `);

        const { data: dbBenchmarkProducts, error } = await query.order('name', { ascending: true });
        
        if (error) {
          console.warn('Error fetching benchmark products:', error);
          return [];
        }

        // Transform the data to match the existing BenchmarkProduct interface
        let transformedProducts = (dbBenchmarkProducts || []).map(product => ({
          id: product.id,
          name: product.name,
          manufacturer: product.manufacturer,
          product_type: product.product_type,
          ecolabels: product.benchmark_product_ecolabels.map(e => ({
            name: e.ecolabel_name,
            status: e.status as 'compliant' | 'partial' | 'not_compliant'
          })),
          retailers: product.benchmark_product_retailers.map(r => ({
            name: r.retailer_name,
            status: r.status as 'compliant' | 'partial' | 'not_compliant'
          })),
          states: {
            count: product.states_count,
            status: product.states_status as 'compliant' | 'partial' | 'not_compliant',
            details: product.states_details || undefined
          }
        }));

        // Apply filters
        if (benchmarkFilters.search) {
          transformedProducts = transformedProducts.filter(product => 
            product.name.toLowerCase().includes(benchmarkFilters.search.toLowerCase()) ||
            product.manufacturer.toLowerCase().includes(benchmarkFilters.search.toLowerCase())
          );
        }

        if (benchmarkFilters.productType) {
          transformedProducts = transformedProducts.filter(product => 
            product.product_type === benchmarkFilters.productType
          );
        }

        if (benchmarkFilters.manufacturer) {
          transformedProducts = transformedProducts.filter(product => 
            product.manufacturer.toLowerCase().includes(benchmarkFilters.manufacturer.toLowerCase())
          );
        }

        if (benchmarkFilters.ecolabels.length > 0) {
          transformedProducts = transformedProducts.filter(product => 
            benchmarkFilters.ecolabels.some(ecolabel =>
              product.ecolabels.some(pe => 
                pe.name === ecolabel && pe.status === 'compliant'
              )
            )
          );
        }

        if (benchmarkFilters.retailers.length > 0) {
          transformedProducts = transformedProducts.filter(product => 
            benchmarkFilters.retailers.some(retailer =>
              product.retailers.some(pr => 
                pr.name === retailer && pr.status === 'compliant'
              )
            )
          );
        }

        if (benchmarkFilters.stateRange) {
          const range = stateRanges.find(r => r.label === benchmarkFilters.stateRange);
          if (range) {
            transformedProducts = transformedProducts.filter(product => 
              product.states.count >= range.min && product.states.count <= range.max
            );
          }
        }

        return transformedProducts;
      } catch (err) {
        console.warn('Error fetching benchmark products:', err);
        return [];
      }
    }
  });

  // Query for chemicals
  const { data: chemicals = [], isLoading: isLoadingChemicals } = useQuery({
    queryKey: ['directory-chemicals', chemicalFilters],
    queryFn: async () => {
      try {
        let query = supabase
          .from('chemicals')
          .select('*');

        const { data: dbChemicals, error } = await query.order('chemical_name', { ascending: true });
        
        if (error) {
          console.warn('Error fetching chemicals:', error);
          return [];
        }

        let filteredChemicals = dbChemicals || [];

        // Apply filters
        if (chemicalFilters.search) {
          filteredChemicals = filteredChemicals.filter(chemical => 
            chemical.chemical_name?.toLowerCase().includes(chemicalFilters.search.toLowerCase()) ||
            chemical.cas_number?.toLowerCase().includes(chemicalFilters.search.toLowerCase()) ||
            chemical.supplier?.toLowerCase().includes(chemicalFilters.search.toLowerCase())
          );
        }

        if (chemicalFilters.chemicalType && chemicalFilters.chemicalType !== 'all') {
          filteredChemicals = filteredChemicals.filter(chemical => 
            chemical.chemical_type === chemicalFilters.chemicalType
          );
        }

        if (chemicalFilters.supplier) {
          filteredChemicals = filteredChemicals.filter(chemical => 
            chemical.supplier?.toLowerCase().includes(chemicalFilters.supplier.toLowerCase())
          );
        }

        // Assign realistic hazard levels based on chemical properties
        const chemicalsWithHazard = filteredChemicals.map(chemical => {
          let hazardLevel: 'low' | 'moderate' | 'high' | 'unknown' = 'unknown';
          
          // High hazard chemicals
          if (['Benzene', 'Phenol', 'Sulfuric Acid', 'Carbon Tetrachloride', 'Formaldehyde'].includes(chemical.chemical_name)) {
            hazardLevel = 'high';
          }
          // Moderate hazard chemicals  
          else if (['Methanol', 'Toluene', 'Ammonia', 'Hydrogen Peroxide', 'Sodium Hydroxide', 'Acetone'].includes(chemical.chemical_name)) {
            hazardLevel = 'moderate';
          }
          // Low hazard chemicals
          else if (['Sodium Chloride', 'Citric Acid', 'Glycerin', 'Titanium Dioxide', 'Isopropyl Alcohol', 'Calcium Carbonate', 'Ethanol', 'Water'].includes(chemical.chemical_name)) {
            hazardLevel = 'low';
          }
          // Unknown/mixed hazard for formulated materials
          else {
            hazardLevel = 'unknown';
          }

          return {
            ...chemical,
            hazard_level: hazardLevel
          };
        });

        // Apply hazard level filter
        let finalFiltered = chemicalsWithHazard;
        if (chemicalFilters.hazardLevel && chemicalFilters.hazardLevel !== 'all') {
          finalFiltered = finalFiltered.filter(chemical => 
            chemical.hazard_level === chemicalFilters.hazardLevel
          );
        }

        return finalFiltered;
      } catch (err) {
        console.warn('Error fetching chemicals:', err);
        return [];
      }
    }
  });

  const handleFilterChange = (newFilters: Partial<DirectoryFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleChemicalFilterChange = (newFilters: Partial<ChemicalFiltersType>) => {
    setChemicalFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleBenchmarkFilterChange = (newFilters: Partial<BenchmarkFiltersType>) => {
    setBenchmarkFilters(prev => ({ ...prev, ...newFilters }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      certificationStatus: '',
      productType: '',
      manufacturer: ''
    });
  };

  const clearChemicalFilters = () => {
    setChemicalFilters({
      search: '',
      chemicalType: '',
      supplier: '',
      hazardLevel: ''
    });
  };

  const clearBenchmarkFilters = () => {
    setBenchmarkFilters({
      search: '',
      productType: '',
      manufacturer: '',
      ecolabels: [],
      retailers: [],
      stateRange: ''
    });
  };

  const getResultsCount = () => {
    if (activeTab === 'chemicals') {
      return chemicals.length;
    }
    return viewMode === 'benchmark' ? filteredBenchmarkProducts.length : products.length;
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNavbar />
      
      <main className="container py-12">
        <DirectoryHeader 
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          resultsCount={getResultsCount()}
        />
        
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as DirectoryTab)} className="mt-8">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="products">Products</TabsTrigger>
            <TabsTrigger value="chemicals">Raw Materials</TabsTrigger>
          </TabsList>
          
          <TabsContent value="products" className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-1">
                {viewMode === 'benchmark' ? (
                  <BenchmarkFilters
                    filters={benchmarkFilters}
                    onFilterChange={handleBenchmarkFilterChange}
                    onClearFilters={clearBenchmarkFilters}
                  />
                ) : (
                  <DirectoryFilters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={clearFilters}
                  />
                )}
              </div>
              
              <div className="lg:col-span-3">
                {viewMode === 'benchmark' ? (
                  <BenchmarkTable products={filteredBenchmarkProducts} />
                ) : (
                  <DirectoryResults
                    products={products}
                    viewMode={viewMode}
                    isLoading={isLoading}
                  />
                )}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="chemicals" className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-1">
                <ChemicalFilters
                  filters={chemicalFilters}
                  onFilterChange={handleChemicalFilterChange}
                  onClearFilters={clearChemicalFilters}
                />
              </div>
              
              <div className="lg:col-span-3">
                <ChemicalResults
                  chemicals={chemicals}
                  viewMode={viewMode === 'benchmark' ? 'card' : viewMode}
                  isLoading={isLoadingChemicals}
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Directory;
